import { now } from '@rematter/common/dateTime';
import type { AssistantAgentStreamEvent } from '@rematter/frontend-lib-api-client';
import type { Getter, PrimitiveAtom, Setter } from 'jotai';

import type { ChatMessageRecord } from '../_graphql/queryAssistantChatMessages';

import { $chats, $updateOptimisticChatTitle } from './chatList.state';

// ========================================================================
// TYPES
// ========================================================================

export type EventContext = {
  messages: ChatMessageRecord[];
  chatId: string | null;
  agentLevel: 'orchestrator' | 'subagent';
  parentToolCallId?: string;
};

// ========================================================================
// QUERY HELPERS
// ========================================================================

export function findMessageWithToolCall(
  messages: ChatMessageRecord[],
  toolCallId: string,
): { msgIdx: number; partIdx: number } | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== 'Assistant') continue;

    const partIdx = msg.parts.findIndex(
      (p) => p.data.type === 'ToolCall' && p.data.toolCallId === toolCallId,
    );

    if (partIdx >= 0) {
      return { msgIdx: i, partIdx };
    }
  }
  return null;
}

export function getLastMessage(
  messages: ChatMessageRecord[],
): ChatMessageRecord | undefined {
  return messages[messages.length - 1];
}

export function isStreamingAssistant(message?: ChatMessageRecord): boolean {
  return message?.role === 'Assistant' && message.id.startsWith('temp-');
}

export function hasToolOutputAvailable(message?: ChatMessageRecord): boolean {
  return Boolean(
    message?.role === 'Assistant' &&
    message.parts.some(
      (p) =>
        p.data.type === 'DynamicTool' &&
        p.data.toolCallState === 'OutputAvailable',
    ),
  );
}

// ========================================================================
// FACTORY HELPERS
// ========================================================================

export function createAssistantMessage(
  chatId: string,
  parts: ChatMessageRecord['parts'],
): ChatMessageRecord {
  return {
    id: `temp-assistant-${Date.now()}`,
    chatId,
    role: 'Assistant',
    parts,
    createdAt: now(),
  };
}

export function createReasoningPart(
  text: string,
  state: 'Streaming' | 'Done' = 'Streaming',
) {
  return {
    id: `temp-part-reasoning-${Date.now()}`,
    sequence: 0,
    data: {
      type: 'Reasoning' as const,
      text,
      state,
    },
  };
}

export function createTextPart(
  text: string,
  state: 'Streaming' | 'Done' = 'Streaming',
) {
  return {
    id: `temp-part-text-${Date.now()}`,
    sequence: 0,
    data: {
      type: 'Text' as const,
      text,
      state,
    },
  };
}

export function createToolCallPart(toolCall: {
  id: string;
  name: string;
  arguments?: Record<string, unknown>;
}) {
  return {
    id: `temp-part-toolcall-${toolCall.id}`,
    sequence: 0,
    data: {
      type: 'ToolCall' as const,
      toolCallId: toolCall.id,
      toolName: toolCall.name,
      toolCallState: 'InputAvailable' as const,
      input: toolCall.arguments,
    },
  };
}

export function createDynamicToolPart(toolResult: {
  id: string;
  name: string;
  result?: Record<string, unknown>;
}) {
  return {
    id: `temp-part-dynamictool-${toolResult.id}`,
    sequence: 0,
    data: {
      type: 'DynamicTool' as const,
      toolCallId: toolResult.id,
      toolName: toolResult.name,
      toolCallState: 'OutputAvailable' as const,
      output: toolResult.result || {},
    },
  };
}

// ========================================================================
// IMMUTABLE UPDATE HELPERS
// ========================================================================

export function updateNestedReasoning(
  messages: ChatMessageRecord[],
  parentToolCallId: string,
  text: string,
): ChatMessageRecord[] {
  const location = findMessageWithToolCall(messages, parentToolCallId);
  if (!location) return messages;

  const { msgIdx, partIdx } = location;
  const message = messages[msgIdx];
  const part = message.parts[partIdx];

  if (part.data.type !== 'ToolCall') return messages;

  const nested = part.data.nested || [];
  const lastNested = nested[nested.length - 1];

  let updatedNested: typeof nested;

  if (
    lastNested?.data.type === 'Reasoning' &&
    lastNested.data.state === 'Streaming'
  ) {
    // Append to existing nested reasoning (immutably)
    updatedNested = [
      ...nested.slice(0, -1),
      {
        ...lastNested,
        data: {
          ...lastNested.data,
          text: lastNested.data.text + text,
        },
      },
    ];
  } else {
    // Add new nested reasoning
    updatedNested = [
      ...nested,
      {
        id: `nested-reasoning-${Date.now()}`,
        sequence: nested.length,
        data: {
          type: 'Reasoning' as const,
          text,
          state: 'Streaming' as const,
        },
      },
    ];
  }

  // Immutably update the message
  const updatedPart = {
    ...part,
    data: {
      ...part.data,
      nested: updatedNested,
    },
  };

  const updatedParts = [...message.parts];
  updatedParts[partIdx] = updatedPart;

  const updatedMessage = {
    ...message,
    parts: updatedParts,
  };

  const result = [...messages];
  result[msgIdx] = updatedMessage;
  return result;
}

export function updateNestedText(
  messages: ChatMessageRecord[],
  parentToolCallId: string,
  text: string,
): ChatMessageRecord[] {
  const location = findMessageWithToolCall(messages, parentToolCallId);
  if (!location) return messages;

  const { msgIdx, partIdx } = location;
  const message = messages[msgIdx];
  const part = message.parts[partIdx];

  if (part.data.type !== 'ToolCall') return messages;

  const nested = part.data.nested || [];
  const lastNested = nested[nested.length - 1];

  let updatedNested: typeof nested;

  if (lastNested?.data.type === 'Text') {
    // Append to existing nested text (immutably)
    updatedNested = [
      ...nested.slice(0, -1),
      {
        ...lastNested,
        data: {
          ...lastNested.data,
          text: lastNested.data.text + text,
        },
      },
    ];
  } else {
    // Add new nested text
    updatedNested = [
      ...nested,
      {
        id: `nested-text-${Date.now()}`,
        sequence: nested.length,
        data: {
          type: 'Text' as const,
          text,
          state: 'Streaming' as const,
        },
      },
    ];
  }

  // Immutably update
  const updatedPart = {
    ...part,
    data: {
      ...part.data,
      nested: updatedNested,
    },
  };

  const updatedParts = [...message.parts];
  updatedParts[partIdx] = updatedPart;

  const updatedMessage = {
    ...message,
    parts: updatedParts,
  };

  const result = [...messages];
  result[msgIdx] = updatedMessage;
  return result;
}

export function addNestedToolCall(
  messages: ChatMessageRecord[],
  parentToolCallId: string,
  toolCall: { id: string; name: string; arguments?: Record<string, unknown> },
): ChatMessageRecord[] {
  const location = findMessageWithToolCall(messages, parentToolCallId);
  if (!location) return messages;

  const { msgIdx, partIdx } = location;
  const message = messages[msgIdx];
  const part = message.parts[partIdx];

  if (part.data.type !== 'ToolCall') return messages;

  const nested = part.data.nested || [];
  const updatedNested = [
    ...nested,
    {
      id: `nested-toolcall-${toolCall.id}`,
      sequence: nested.length,
      data: {
        type: 'ToolCall' as const,
        toolCallId: toolCall.id,
        toolName: toolCall.name,
        toolCallState: 'InputAvailable' as const,
        input: toolCall.arguments || {},
      },
    },
  ];

  // Immutably update
  const updatedPart = {
    ...part,
    data: {
      ...part.data,
      nested: updatedNested,
    },
  };

  const updatedParts = [...message.parts];
  updatedParts[partIdx] = updatedPart;

  const updatedMessage = {
    ...message,
    parts: updatedParts,
  };

  const result = [...messages];
  result[msgIdx] = updatedMessage;
  return result;
}

export function addNestedToolResult(
  messages: ChatMessageRecord[],
  parentToolCallId: string,
  toolResult: { id: string; name: string; result?: Record<string, unknown> },
): ChatMessageRecord[] {
  const location = findMessageWithToolCall(messages, parentToolCallId);
  if (!location) return messages;

  const { msgIdx, partIdx } = location;
  const message = messages[msgIdx];
  const part = message.parts[partIdx];

  if (part.data.type !== 'ToolCall') return messages;

  const nested = part.data.nested || [];
  const updatedNested = [
    ...nested,
    {
      id: `nested-toolresult-${toolResult.id}`,
      sequence: nested.length,
      data: {
        type: 'DynamicTool' as const,
        toolCallId: toolResult.id,
        toolName: toolResult.name,
        toolCallState: 'OutputAvailable' as const,
        output: toolResult.result || {},
      },
    },
  ];

  // Immutably update
  const updatedPart = {
    ...part,
    data: {
      ...part.data,
      nested: updatedNested,
    },
  };

  const updatedParts = [...message.parts];
  updatedParts[partIdx] = updatedPart;

  const updatedMessage = {
    ...message,
    parts: updatedParts,
  };

  const result = [...messages];
  result[msgIdx] = updatedMessage;
  return result;
}

export function finalizeStreamingParts(
  messages: ChatMessageRecord[],
  messageIdx: number,
): ChatMessageRecord[] {
  const message = messages[messageIdx];
  if (!message.id.startsWith('temp-')) return messages;

  const parts = message.parts.map((part) => ({
    ...part,
    data:
      part.data.type === 'Text' || part.data.type === 'Reasoning'
        ? { ...part.data, state: 'Done' as const }
        : part.data,
  }));

  const updatedMessage = {
    ...message,
    id: message.id.replace('temp-', 'stream-'),
    parts,
  };

  const result = [...messages];
  result[messageIdx] = updatedMessage;
  return result;
}

// ========================================================================
// EVENT HANDLERS
// ========================================================================

export function handleConversationTitle(
  event: AssistantAgentStreamEvent,
  context: EventContext,
  set: Setter,
): void {
  const title = event.conversationTitle;
  if (title && context.chatId) {
    set($updateOptimisticChatTitle, { chatId: context.chatId, title });
  }
}

export function handleThinking(
  event: AssistantAgentStreamEvent,
  context: EventContext,
): ChatMessageRecord[] {
  const { messages, chatId, agentLevel, parentToolCallId } = context;
  const thinkingStep = event.thinkingStep || '';

  // Subagent thinking -> always nest under parent tool call for debugging
  if (agentLevel === 'subagent' && parentToolCallId) {
    return updateNestedReasoning(messages, parentToolCallId, thinkingStep);
  }

  // Orchestrator thinking -> append to current or create new message
  const lastMessage = getLastMessage(messages);
  const needsNewTurn =
    !lastMessage ||
    lastMessage.role === 'User' ||
    (lastMessage.role === 'Assistant' && !lastMessage.id.startsWith('temp-'));

  if (needsNewTurn) {
    // Create NEW Assistant message with Reasoning part
    const newMsg = createAssistantMessage(chatId ?? '', [
      createReasoningPart(thinkingStep, 'Streaming'),
    ]);
    return [...messages, newMsg];
  }

  // Append to existing Reasoning part or create new one
  const reasoningPartIndex = lastMessage.parts.findIndex(
    (p) => p.data.type === 'Reasoning',
  );

  if (reasoningPartIndex >= 0) {
    // Immutably update existing reasoning part
    const updated = [...messages];
    const last = { ...updated[updated.length - 1] };
    const reasoningPart = last.parts[reasoningPartIndex];

    if (reasoningPart.data.type === 'Reasoning') {
      last.parts = [...last.parts];
      last.parts[reasoningPartIndex] = {
        ...reasoningPart,
        data: {
          ...reasoningPart.data,
          text: reasoningPart.data.text + thinkingStep,
        },
      };
    }

    updated[updated.length - 1] = last;
    return updated;
  }

  // Create new reasoning part
  const updated = [...messages];
  const last = { ...updated[updated.length - 1] };
  last.parts = [
    ...last.parts,
    {
      ...createReasoningPart(thinkingStep, 'Streaming'),
      sequence: last.parts.length,
    },
  ];
  updated[updated.length - 1] = last;
  return updated;
}

export function handleMessage(
  event: AssistantAgentStreamEvent,
  context: EventContext,
): ChatMessageRecord[] {
  const { messages, chatId, agentLevel, parentToolCallId } = context;
  const content = event.content || '';

  // Subagent message -> always nest under parent tool call for debugging
  if (agentLevel === 'subagent' && parentToolCallId) {
    return updateNestedText(messages, parentToolCallId, content);
  }

  // Orchestrator message
  const lastMessage = getLastMessage(messages);
  const isStreamingAssist = isStreamingAssistant(lastMessage);
  const hasToolOutput = hasToolOutputAvailable(lastMessage);

  if (isStreamingAssist && !hasToolOutput && lastMessage) {
    const textPartIndex = lastMessage.parts.findIndex(
      (p) => p.data.type === 'Text',
    );

    if (textPartIndex >= 0) {
      // Immutably update existing text part
      const updated = [...messages];
      const last = { ...updated[updated.length - 1] };
      const textPart = last.parts[textPartIndex];

      if (textPart.data.type === 'Text') {
        last.parts = [...last.parts];
        last.parts[textPartIndex] = {
          ...textPart,
          data: {
            ...textPart.data,
            text: textPart.data.text + content,
          },
        };
      }

      updated[updated.length - 1] = last;
      return updated;
    }

    // Create new text part
    const updated = [...messages];
    const last = { ...updated[updated.length - 1] };
    last.parts = [
      ...last.parts,
      { ...createTextPart(content, 'Streaming'), sequence: last.parts.length },
    ];
    updated[updated.length - 1] = last;
    return updated;
  }

  // Create new Assistant message with Text part
  const newMsg = createAssistantMessage(chatId ?? '', [
    createTextPart(content, 'Streaming'),
  ]);
  return [...messages, newMsg];
}

export function handleToolCall(
  event: AssistantAgentStreamEvent,
  context: EventContext,
): ChatMessageRecord[] {
  const { messages, chatId, agentLevel, parentToolCallId } = context;
  const toolCall = event.toolCall;
  if (!toolCall) return messages;

  // Subagent tool call -> always nest under parent tool call for debugging
  if (agentLevel === 'subagent' && parentToolCallId) {
    return addNestedToolCall(messages, parentToolCallId, {
      id: toolCall.id,
      name: toolCall.name,
      arguments: toolCall.arguments,
    });
  }

  // Orchestrator tool call
  const lastMessage = getLastMessage(messages);
  const isStreamingAssist = isStreamingAssistant(lastMessage);

  if (isStreamingAssist) {
    // Add/update ToolCall part in existing message
    const updated = [...messages];
    const last = { ...updated[updated.length - 1] };
    const existingPartIndex = last.parts.findIndex(
      (p) => p.data.type === 'ToolCall' && p.data.toolCallId === toolCall.id,
    );

    if (existingPartIndex >= 0) {
      const part = last.parts[existingPartIndex];
      if (part.data.type === 'ToolCall') {
        last.parts = [...last.parts];
        last.parts[existingPartIndex] = {
          ...part,
          data: {
            ...part.data,
            input: toolCall.arguments,
          },
        };
      }
    } else {
      last.parts = [
        ...last.parts,
        {
          ...createToolCallPart({
            id: toolCall.id,
            name: toolCall.name,
            arguments: toolCall.arguments,
          }),
          sequence: last.parts.length,
        },
      ];
    }

    updated[updated.length - 1] = last;
    return updated;
  }

  // Create new Assistant message with ToolCall part
  const newMsg = createAssistantMessage(chatId ?? '', [
    createToolCallPart({
      id: toolCall.id,
      name: toolCall.name,
      arguments: toolCall.arguments,
    }),
  ]);
  return [...messages, newMsg];
}

export function handleToolResult(
  event: AssistantAgentStreamEvent,
  context: EventContext,
): ChatMessageRecord[] {
  const { messages, chatId, agentLevel, parentToolCallId } = context;
  const toolCall = event.toolCall;
  if (!toolCall) return messages;

  // Subagent tool result -> always nest under parent tool call for debugging
  if (agentLevel === 'subagent' && parentToolCallId) {
    return addNestedToolResult(messages, parentToolCallId, {
      id: toolCall.id,
      name: toolCall.name,
      result: toolCall.result || undefined,
    });
  }

  // Orchestrator tool result - keep within current Assistant message
  const updated = [...messages];
  if (updated.length > 0 && updated[updated.length - 1].role === 'Assistant') {
    const last = { ...updated[updated.length - 1] };
    const toolIdx = last.parts.findIndex(
      (p) => p.data.type === 'ToolCall' && p.data.toolCallId === toolCall.id,
    );

    if (toolIdx >= 0) {
      // Add dynamic tool result part
      last.parts = [
        ...last.parts,
        {
          ...createDynamicToolPart({
            id: toolCall.id,
            name: toolCall.name,
            result: toolCall.result || undefined,
          }),
          sequence: last.parts.length,
        },
      ];

      // Mark streaming parts as Done
      last.parts = last.parts.map((part) => ({
        ...part,
        data:
          part.data.type === 'Text' || part.data.type === 'Reasoning'
            ? { ...part.data, state: 'Done' as const }
            : part.data,
      }));

      // Finalize this assistant message
      if (last.id.startsWith('temp-')) {
        last.id = last.id.replace('temp-', 'stream-');
      }

      updated[updated.length - 1] = last;
      return updated;
    }

    // Fallback: create assistant message with result (should be rare)
    const resultMsg = createAssistantMessage(chatId ?? '', [
      createDynamicToolPart({
        id: toolCall.id,
        name: toolCall.name,
        result: toolCall.result || undefined,
      }),
    ]);
    resultMsg.id = `stream-toolresult-${toolCall.id}-${Date.now()}`;
    return [...updated, resultMsg];
  }

  return messages;
}

export function handleRetry(
  messages: ChatMessageRecord[],
): ChatMessageRecord[] {
  const updated = [...messages];
  if (
    updated.length > 0 &&
    updated[updated.length - 1].id.startsWith('temp-')
  ) {
    updated.pop();
  }
  return updated;
}

export function handleError(
  event: AssistantAgentStreamEvent,
  set: Setter,
): void {
  if (event.error) {
    set($streamingError, { error: event.error });
    setTimeout(() => set($streamingError, null), 10000);
  }
  set($isAgentWorking, false);
}

export function handleComplete(
  messages: ChatMessageRecord[],
  set: Setter,
  get: Getter,
): ChatMessageRecord[] {
  // Finalize any pending Assistant message
  let updated = [...messages];
  if (
    updated.length > 0 &&
    updated[updated.length - 1].id.startsWith('temp-')
  ) {
    updated = finalizeStreamingParts(updated, updated.length - 1);
  }

  set($isAgentWorking, false);
  get($chats).refetch();
  return updated;
}

let $streamingError: PrimitiveAtom<{
  error: string;
  errorCode?: string;
} | null>;
let $isAgentWorking: PrimitiveAtom<boolean>;

export function initializeAtomRefs(
  streamingErrorAtom: PrimitiveAtom<{
    error: string;
    errorCode?: string;
  } | null>,
  isAgentWorkingAtom: PrimitiveAtom<boolean>,
) {
  $streamingError = streamingErrorAtom;
  $isAgentWorking = isAgentWorkingAtom;
}
